import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Context } from '../../../context/UserContext';
import tableThemeCustom from '../../form/tableThemeCustom';
import * as API from '../../../api/pocos';
import { usePermissoesModule } from './modules/usePermissoesModule';
import { mensagemErro, nomeBem } from './modules/valores';
import Lista from './components/Lista';
import Arquivos from './components/Arquivos';
import CadastroForm from './forms/CadastroForm';
import MedicaoForm from './forms/MedicaoForm';
import OutorgaForm from './forms/OutorgaForm';

export default function Index() {
    const p = usePermissoesModule('Pocos');
    if (!p.autorizado) return <Alert severity={p.pronto ? 'warning' : 'info'}>{p.mensagem}</Alert>;
    return <Rotina key={localStorage.getItem('empresaId')} p={p} />;
}
function Rotina({ p }) {
    const { parametro } = useContext(Context);
    const theme = tableThemeCustom(),
        isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [bens, setBens] = useState([]),
        [bemId, setBemId] = useState(null),
        [aba, setAba] = useState('medicoes');
    const [revision, setRevision] = useState(0),
        [loading, setLoading] = useState(true),
        [busy, setBusy] = useState(false);
    const [erro, setErro] = useState(''),
        [notice, setNotice] = useState(null),
        [modal, setModal] = useState(null);
    const lock = useRef(false),
        request = useRef(0);
    const bem = bens.find((b) => b.id === bemId) || null;
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setErro('');
        API.consultar('bens/listar', {}, { signal: controller.signal })
            .then((r) => {
                if (controller.signal.aborted) return;

                if (!Array.isArray(r?.dados)) {
                    throw new Error('A listagem de poços não retornou o array "dados".');
                }

                setBens(r.dados);
                setBemId((prev) => (r.dados.some((b) => b.id === prev) ? prev : (r.dados[0]?.id ?? null)));
            })
            .catch((e) => {
                if (!controller.signal.aborted) {
                    setBens([]);
                    setErro(mensagemErro(e));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [revision]);
    useEffect(
        () => () => {
            request.current += 1;
        },
        []
    );
    async function abrir(action, row, recurso = aba) {
        if (lock.current) return;
        const needsInclude = ['novo', 'versao', 'habilitar'].includes(action);
        const needsCancel = ['cancelar', 'encerrar', 'excluir'].includes(action);
        if (
            (needsInclude && !p.incluir) ||
            (needsCancel && !p.cancelar) ||
            (['regularizar', 'lacre', 'configurar'].includes(action) && !p.editar)
        )
            return;
        lock.current = true;
        setBusy(true);
        setErro('');
        const current = ++request.current;
        try {
            let next;
            if (action === 'habilitar')
                next = { tipo: 'bem', candidatos: (await API.consultar('bens/listar', { isPoco: false })).dados };
            else if (action === 'arquivos' && recurso === 'medicoes') next = { tipo: 'arquivos', original: row };
            else if (action === 'configurar') next = { tipo: 'bem', original: bem };
            else if (action === 'novo' && recurso === 'medidores') next = { tipo: 'medidor' };
            else if (['lacre', 'encerrar'].includes(action)) next = { tipo: action, original: row };
            else {
                const original = row ? (await API.consultar(`${recurso}/editar/${row.uuid}`)).dados : {};
                if (['cancelar', 'excluir'].includes(action)) next = { tipo: action, original };
                else if (action === 'regularizar')
                    next = { tipo: action, original, outorgas: await API.opcoes('outorgas', bem.id) };
                else if (recurso === 'medicoes')
                    next = {
                        tipo: 'medicao',
                        original,
                        medidores: await API.opcoes('medidores', bem.id),
                        readOnly: Boolean(row) && (!p.editar || original.situacao === 'CANCELADA'),
                    };
                else {
                    const outorgas = await API.opcoes('outorgas', bem.id);
                    next = { tipo: 'outorga', original, outorgas, readOnly: Boolean(row) && !p.editar };
                    if (action === 'versao')
                        next = {
                            ...next,
                            readOnly: false,
                            original: {
                                // Novo ato: não copiar vigência, confirmação, protocolo ou limites sem conferência.
                                orgao: original.orgao,
                                tipoAto: 'RENOVACAO',
                                situacao: 'RASCUNHO',
                                versao: Number(original.versao) + 1,
                                outorgaAnteriorId: original.id,
                                condicionantes: (original.condicionantes || []).map(({ id, uuid, outorgaId, ...c }) => ({
                                    ...c,
                                })),
                            },
                        };
                }
            }
            if (current === request.current) setModal({ ...next, recurso, bem });
        } catch (e) {
            if (current === request.current) setErro(mensagemErro(e));
        } finally {
            lock.current = false;
            if (current === request.current) setBusy(false);
        }
    }
    function saved(result) {
        const avisos = [...(result.avisos || [])];
        if (result.dados?.pendenciaOutorga) avisos.unshift(`Regularização da outorga pendente: ${result.dados.pendenciaOutorga}`);
        setNotice({ message: result.message || 'Registro salvo.', avisos });
        setRevision((n) => n + 1);
        if (['medicao', 'outorga'].includes(modal.tipo))
            setModal({
                tipo: 'arquivos',
                recurso: modal.tipo === 'medicao' ? 'medicoes' : 'outorgas',
                original: result.dados,
                recemSalvo: true,
            });
        else setModal(null);
    }
    const common = { bem, onClose: () => setModal(null), onSaved: saved };
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                    Gestão de poços
                </Typography>
                {(loading || busy) && <LinearProgress sx={{ mb: 2 }} />}
                {erro && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {erro}
                    </Alert>
                )}
                {notice && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="success" onClose={() => setNotice(null)}>
                            {notice.message}
                        </Alert>
                        {notice.avisos.map((a, i) => (
                            <Alert severity="warning" key={i}>
                                {String(a).replaceAll('_', ' ')}
                            </Alert>
                        ))}
                    </Box>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <Autocomplete
                        options={bens}
                        value={bem}
                        getOptionLabel={nomeBem}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        sx={{ flex: 1, minWidth: 240 }}
                        disabled={busy || loading || Boolean(modal)}
                        onChange={(_, value) => {
                            setBemId(value?.id ?? null);
                            setNotice(null);
                        }}
                        renderInput={(params) => <TextField {...params} label="Poço" size="small" />}
                    />
                    <Button disabled={!p.incluir || busy || loading} onClick={() => abrir('habilitar')}>
                        Habilitar bem
                    </Button>
                    <Button disabled={!p.editar || !bem || busy || loading} onClick={() => abrir('configurar')}>
                        Dados técnicos
                    </Button>
                    <Button disabled={busy || loading} onClick={() => setRevision((n) => n + 1)}>
                        Atualizar
                    </Button>
                </Stack>
                {!loading && !bem && (
                    <Alert severity="info">Selecione um poço ou habilite um bem existente para iniciar o acompanhamento.</Alert>
                )}
                {bem && (
                    <>
                        <Tabs
                            value={aba}
                            onChange={(_, value) => setAba(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ mb: 2 }}
                        >
                            <Tab value="medicoes" label="Medições" disabled={busy} />
                            <Tab value="outorgas" label="Outorgas" disabled={busy} />
                            <Tab value="medidores" label="Medidores e lacres" disabled={busy} />
                        </Tabs>
                        <Lista
                            key={`${bem.id}:${aba}:${revision}`}
                            recurso={aba}
                            bem={bem}
                            p={p}
                            parametro={parametro}
                            isMobile={isMobile}
                            onAction={abrir}
                            busy={busy}
                        />
                    </>
                )}
                {modal?.tipo === 'medicao' && (
                    <MedicaoForm key={`${modal.bem.id}:${modal.original?.uuid || 'nova'}`} {...common} {...modal} />
                )}
                {modal?.tipo === 'outorga' && <OutorgaForm {...common} {...modal} />}
                {modal && !['medicao', 'outorga', 'arquivos'].includes(modal.tipo) && <CadastroForm {...common} {...modal} />}
                {modal?.tipo === 'arquivos' && (
                    <Dialog open fullWidth fullScreen={isMobile} maxWidth="md" onClose={() => setModal(null)}>
                        <DialogTitle>
                            {modal.recemSalvo ? 'Registro salvo — anexos' : `Fotos e anexos — ${nomeBem(modal.bem || bem)}`}
                            {modal.recurso === 'medicoes' && modal.original.dataMedicao && (
                                <Typography component="div" variant="body2">
                                    Medição: {String(modal.original.dataMedicao).slice(0, 10).split('-').reverse().join('/')}
                                    {modal.original.horaMedicao ? ` às ${modal.original.horaMedicao}` : ''}
                                </Typography>
                            )}
                        </DialogTitle>
                        <DialogContent dividers>
                            <Arquivos
                                tipo={modal.recurso}
                                registro={modal.original}
                                podeAnexar={
                                    modal.original.situacao !== 'CANCELADA' && (p.editar || (modal.recemSalvo && p.incluir))
                                }
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setModal(null)}>Fechar</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Box>
        </ThemeProvider>
    );
}
